/**
 * Scheduler Hook
 *
 * Merges scheduled tasks + Google Calendar events into unified ScheduleItems,
 * then computes overlap layout for each day column.
 */

import { useMemo } from 'react';
import { startOfDay, isSameDay } from 'date-fns';
import type { Task } from '@/types';
import {
  type ScheduleItem,
  type PositionedItem,
  calculateOverlapLayout,
  PIXELS_PER_MS,
  DEFAULT_START_HOUR,
} from '@/utils/scheduleLayout';

export type { ScheduleItem, PositionedItem };

interface CalendarEventInput {
  id: string;
  summary: string | null;
  startTime: number;
  endTime: number;
  allDay: boolean;
  colorId: string | null;
  linkedTaskId: string | null;
  status: string;
  htmlLink: string | null;
}

interface UseSchedulerOptions {
  tasks: Task[];
  calendarEvents?: CalendarEventInput[];
  days: Date[];
  startHour?: number;
  endHour?: number;
}

interface UseSchedulerReturn {
  /** Map of date ISO string -> positioned items for that day */
  dayColumns: Map<string, PositionedItem[]>;
  /** All-day items (tasks + events) */
  allDayItems: ScheduleItem[];
  /** All schedule items before positioning */
  allItems: ScheduleItem[];
  /** Tasks with dueDate on visible days but without scheduledStart/End */
  unscheduledTasks: Task[];
}

/**
 * Convert a task with scheduledStart/End into a ScheduleItem.
 */
function taskToScheduleItem(task: Task): ScheduleItem | null {
  if (!task.scheduledStart || !task.scheduledEnd) return null;
  return {
    id: task.id,
    title: task.name,
    startTime: task.scheduledStart,
    endTime: task.scheduledEnd,
    isAllDay: false,
    type: 'task',
    color: task.priority === 'urgent' ? '#f43f5e'
      : task.priority === 'high' ? '#f59e0b'
      : task.priority === 'normal' ? '#3b82f6'
      : task.priority === 'low' ? '#10b981'
      : null,
    isReadOnly: false,
    taskData: task,
  };
}

/**
 * Convert a Google Calendar event into a ScheduleItem.
 */
function eventToScheduleItem(event: CalendarEventInput): ScheduleItem {
  return {
    id: `gcal-${event.id}`,
    title: event.summary || '(Nessun titolo)',
    startTime: event.startTime,
    endTime: event.endTime,
    isAllDay: event.allDay,
    type: 'calendar_event',
    color: event.colorId || '#8b5cf6',
    isReadOnly: !event.linkedTaskId,
    eventData: event,
  };
}

export function useScheduler({
  tasks,
  calendarEvents = [],
  days,
  startHour = DEFAULT_START_HOUR,
}: UseSchedulerOptions): UseSchedulerReturn {
  // Convert tasks with scheduling data to schedule items
  const taskItems = useMemo((): ScheduleItem[] => {
    return tasks
      .map(taskToScheduleItem)
      .filter((item): item is ScheduleItem => item !== null);
  }, [tasks]);

  // Convert calendar events to schedule items
  const eventItems = useMemo((): ScheduleItem[] => {
    return calendarEvents
      .filter((e) => e.status !== 'cancelled')
      .map(eventToScheduleItem);
  }, [calendarEvents]);

  // Merge all items
  const allItems = useMemo(
    () => [...taskItems, ...eventItems],
    [taskItems, eventItems]
  );

  // Separate all-day items
  const allDayItems = useMemo(
    () => allItems.filter((item) => item.isAllDay),
    [allItems]
  );

  // Compute positioned items per day
  const dayColumns = useMemo(() => {
    const map = new Map<string, PositionedItem[]>();

    for (const day of days) {
      const dayKey = day.toISOString();
      const dayStartMs = startOfDay(day).getTime() + startHour * 60 * 60 * 1000;

      // Filter items that fall on this day
      const dayItems = allItems.filter((item) => {
        if (item.isAllDay) return false;
        return (
          isSameDay(new Date(item.startTime), day) ||
          isSameDay(new Date(item.endTime), day)
        );
      });

      // Clamp items to the day boundaries
      const clampedItems = dayItems.map((item) => {
        const dayStartBound = startOfDay(day).getTime();
        const dayEndBound = dayStartBound + 24 * 60 * 60 * 1000;
        return {
          ...item,
          startTime: Math.max(item.startTime, dayStartBound),
          endTime: Math.min(item.endTime, dayEndBound),
        };
      });

      const positioned = calculateOverlapLayout(
        clampedItems,
        dayStartMs,
        PIXELS_PER_MS
      );

      map.set(dayKey, positioned);
    }

    return map;
  }, [allItems, days, startHour]);

  // Tasks with dueDate on visible days but no scheduling
  const unscheduledTasks = useMemo((): Task[] => {
    return tasks.filter(task => {
      if (task.scheduledStart && task.scheduledEnd) return false;
      if (!task.dueDate) return false;
      return days.some(day => isSameDay(new Date(task.dueDate!), day));
    });
  }, [tasks, days]);

  return {
    dayColumns,
    allDayItems,
    allItems,
    unscheduledTasks,
  };
}

/**
 * Snap a duration to the nearest 15-minute increment.
 */
export function snapTo15Min(ms: number): number {
  const fifteenMin = 15 * 60 * 1000;
  return Math.round(ms / fifteenMin) * fifteenMin;
}

/**
 * Default duration for new scheduled items: 1 hour.
 */
export const DEFAULT_DURATION_MS = 60 * 60 * 1000;

/**
 * Minimum duration: 15 minutes.
 */
export const MIN_DURATION_MS = 15 * 60 * 1000;

/**
 * Snap interval for dragging: 15 minutes.
 */
export const SNAP_INTERVAL_MS = 15 * 60 * 1000;
